import {
  Alert,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  MenuItem,
  Pagination,
  Typography,
  useTheme
} from '@mui/material';
import { useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useForm } from 'react-hook-form';

import PrimaryButton from '../ui/buttons/primaryButton';
import SecondaryButton from '../ui/buttons/secondaryButton';
import Form from '../ui/forms';
import Input from '../ui/forms/fields/input';
import Select from '../ui/forms/fields/select';
import Loader from '../ui/loader';

function Clients({
  clients = [],
  loading = false,
  providers = [],
  pageIndex = 0,
  totalPages = 1,
  totalCount = 0,
  onSearch,
  onPageChange,
  onClear,
  onViewClient
}) {
  const theme = useTheme();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('all');

  const { register, handleSubmit, reset, control } = useForm({
    defaultValues: {
      term: '',
      serviceProvider: 'all'
    }
  });

  // ✅ strict advisor check
  const hasMultipleAdvisors = (client) => {
    if (!client?.accountInfos?.length) return false;

    const partnerIds = client.accountInfos
      .filter((acc) => acc?.partnerClient?.partnerId)
      .map((acc) => acc.partnerClient.partnerId);

    if (!partnerIds.length) return false;

    return new Set(partnerIds).size > 1;
  };

  const handleSearch = (data) => {
    const payload = {
      term: data.term || '',
      provider:
        data.serviceProvider && data.serviceProvider !== 'all' ? data.serviceProvider : null,
      pageIndex: 0
    };

    setSearchTerm(payload.term);
    setSelectedProvider(data.serviceProvider);

    onSearch?.(payload);
  };

  const handlePage = (event, value) => {
    const apiPageIndex = value - 1;

    onPageChange?.({
      term: searchTerm,
      provider: selectedProvider !== 'all' ? selectedProvider : null,
      pageIndex: apiPageIndex
    });
  };

  const handleClear = () => {
    reset({
      term: '',
      serviceProvider: 'all'
    });

    setSearchTerm('');
    setSelectedProvider('all');

    onClear?.();
  };

  const formatName = (name = '') => {
    return name
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        if (index === 0 && word.length < 4) {
          return word.toUpperCase();
        }

        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  return (
    <Box width="100%">
      {/* SEARCH */}
      <Form>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Select label="Service Provider" control={control} name="serviceProvider">
            <MenuItem value="all">All</MenuItem>

            {providers.map((provider) => (
              <MenuItem key={provider.id} value={provider.id}>
                {provider.clientName}
              </MenuItem>
            ))}
          </Select>

          <Input label="Search (Name, ID Number / Registration Number)" {...register('term')} />

          <Box display="flex" gap={1}>
            <PrimaryButton onClick={handleClear}>CLEAR SEARCH</PrimaryButton>
            <SecondaryButton onClick={handleSubmit(handleSearch)}>SEARCH</SecondaryButton>
          </Box>
        </Box>
      </Form>

      {/* RESULTS */}
      {loading ? (
        <Loader loading={true} message="Searching ..." />
      ) : clients.length > 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center">
          {/* TOP PAGINATION */}
          {totalPages > 1 && (
            <Box mt={2}>
              <Pagination
                count={totalPages}
                page={pageIndex + 1}
                onChange={handlePage}
                color="primary"
              />
            </Box>
          )}

          <Typography m={2} variant="h6">
            {totalCount} Results
          </Typography>

          {clients.map((client) => (
            <Card
              key={client.id}
              sx={{
                margin: 1,
                width: '100%',
                maxWidth: 700,
                background: theme.palette.background.paper,
                boxShadow: 'none'
              }}>
              <CardHeader title={formatName(client.name)} subheader={client.clientId} />

              <CardContent>
                {client.accountInfos?.map((acc) => (
                  <Typography key={acc.accountCode} variant="body2" fontSize="10px">
                    {acc.source} ({acc.accountCode}) - {acc.partnerClient?.partnerName} (
                    {acc.partnerCode})
                  </Typography>
                ))}
              </CardContent>

              {/* SMALL INFO ALERT UNDER TEXT */}
              {hasMultipleAdvisors(client) && (
                <Box sx={{ px: 2 }}>
                  <Alert
                    icon={
                      <InfoOutlinedIcon
                        sx={{
                          color: theme.palette.accent.main,
                          fontSize: '14px'
                        }}
                      />
                    }
                    severity="info"
                    sx={{
                      mt: 0.5,
                      mb: 1,
                      py: 0,
                      px: 0,
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: theme.palette.accent.main,

                      '& .MuiAlert-message': {
                        fontSize: '10px',
                        lineHeight: 1.2,
                        padding: 0
                      },

                      '& .MuiAlert-icon': {
                        marginRight: '6px',
                        padding: 0
                      }
                    }}>
                    Client is linked to more than one advisor, ensure the correct method of
                    communication is followed
                  </Alert>
                </Box>
              )}

              <CardActions sx={{ justifyContent: 'center' }}>
                <PrimaryButton onClick={() => onViewClient?.(client)}>View</PrimaryButton>
              </CardActions>
            </Card>
          ))}

          {/* BOTTOM PAGINATION */}
          {totalPages > 1 && (
            <Box mt={2}>
              <Pagination
                count={totalPages}
                page={pageIndex + 1}
                onChange={handlePage}
                color="primary"
              />
            </Box>
          )}
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" alignItems="center" m={3}>
          <Typography variant="h6">No Results Found</Typography>
        </Box>
      )}
    </Box>
  );
}

export default Clients;
